import {
  Box, Button, Grid, Stack,
} from '@mui/material';
import { useCallback, useMemo } from 'react';
import AddCircleRoundedIcon from '@mui/icons-material/AddCircleRounded';
import BlockProps from '../../../models/BlockProps';
import CloseButton from '../../Buttons/Close';
import BlockTableData, { BlockTableVariant } from './BlockTableData';
import TableItem from './components/TableItem';
import BlockTableItemData from './BlockTableItemData';
import Utils from '../../../utils/Utils';
import EditableText from '../../Form/EditableText';
import MoveButtons, { MoveButtonDirection } from '../../Buttons/Move';

const BlockTable = ({
  data, isEditing, isTemplate, editHandler,
}: BlockProps) => {
  const props = (data.data as BlockTableData);

  const handleMoveItem = useCallback((direction: MoveButtonDirection, index: number) => {
    if (direction === MoveButtonDirection.Up) {
      props.items = Utils.moveArrayItem(props.items, index, index - 1);
      editHandler(data.id, props);
    } else {
      props.items = Utils.moveArrayItem(props.items, index, index + 1);
      editHandler(data.id, props);
    }
  }, [data.id, editHandler, props]);

  const tableHeader = useMemo(() => {
    if (props.headerItem) {
      return (
        <Grid
          item
          container
          sx={{
            position: 'relative',
            height: '100%',
          }}
        >
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.headerItem = undefined;
              editHandler(data.id, props);
            }}
          />
          <TableItem
            isHeader
            item={props.headerItem}
            isEditing={isEditing}
            isTemplate={isTemplate}
            variant={props.variant}
            handleUpdateTitle={(text: string) => {
              props.headerItem.title.value = text;
              editHandler(data.id, props);
            }}
            handleUpdateText={(text: string) => {
              props.headerItem.text.value = text;
              editHandler(data.id, props);
            }}
          />
        </Grid>
      );
    }
    return [];
  }, [data.id, editHandler, isEditing, isTemplate, props]);

  const tableItems = useMemo(() => {
    if (props.items && props.items.length > 0) {
      return props.items.map((item, index) => (
        <Grid
          key={item.id}
          item
          container
          sx={{
            position: 'relative',
            height: '100%',
          }}
        >
          <MoveButtons
            show={isEditing}
            updateHandler={(direction: MoveButtonDirection) => {
              handleMoveItem(direction, index);
            }}
            hideUp={index === 0}
            hideDown={index === props.items.length - 1}
          />
          <CloseButton
            show={isEditing}
            updateHandler={() => {
              props.items.splice(index, 1);
              editHandler(data.id, props);
            }}
          />
          <TableItem
            item={item}
            isEditing={isEditing}
            isTemplate={isTemplate}
            isFirst={index === 0}
            variant={props.variant}
            handleUpdateTitle={(text: string) => {
              props.items[index].title.value = text;
              editHandler(data.id, props);
            }}
            handleUpdateText={(text: string) => {
              props.items[index].text.value = text;
              editHandler(data.id, props);
            }}
          />
        </Grid>
      ));
    }
    return [];
  }, [data.id, editHandler, handleMoveItem, isEditing, isTemplate, props]);

  return (
    <Grid
      container
      spacing={0}
      direction="column"
      alignItems="center"
      justifyContent="center"
      sx={{
        minHeight: '120px',
        overflow: 'hidden',
        p: isEditing || isTemplate ? 1.5 : 0,
      }}
    >
      {props.title.value && (
        <Grid item alignItems="center" xs={12} sx={{ minHeight: '46px', marginBottom: 2 }}>
          <Box sx={Utils.hoverBorder(isEditing)}>
            <EditableText
              text={props.title.value}
              isEditable={isEditing && !isTemplate}
              updateHandler={(value) => {
                props.title.value = value;
                editHandler(data.id, props);
              }}
            />
            <CloseButton
              show={isEditing}
              updateHandler={() => {
                props.title.value = undefined;
                editHandler(data.id, props);
              }}
            />
          </Box>
        </Grid>
      )}
      {props.variant === BlockTableVariant.Default && (
        <>
          {tableHeader}
        </>
      )}
      {tableItems}
      {isEditing && (
        <Stack spacing={0} sx={{ border: '1px dashed lightGray', marginTop: '12px', width: 'calc(100% - 24px)' }}>
          <Button
            color="primary"
            onClick={() => {
              props.items.push(BlockTableItemData.getDefault());
              editHandler(data.id, props);
            }}
          >
            <AddCircleRoundedIcon />
          </Button>
        </Stack>
      )}
    </Grid>
  );
};

export default BlockTable;
